import {
	autoinject,
	bindable,
	computedFrom,
	Container,
	observable
} from "aurelia-framework";
import { NodeModel } from "../../models/node-model";
import {
	ServiceDataItemHandler,
	MeasureService
} from "../measures/services/measure-service";
import { ScopeTypeGroup } from "../defaultMeasures/scope-type-group-service";
import { ServiceStructureService } from "../services/service-structure-service";
import { CustomerService } from "./customer-service";
import {
	ReportedServiceDataItemService,
	DataItemOption,
	DataItemSelectionResult,
	IServiceDataItems
} from "./reported-service-data-item-service";
import { max, filter } from "lodash";
import { IPerformanceIndicatorAM } from "../performance-indicators/performance-indicator-models";
import {
	ReportedServiceDataItemType,
	AttachedDataItem
} from "../reported-service-data-items/reported-service-data-item-models";
import { PerformanceIndicatorService } from "../performance-indicators/performance-indicator-service";
import { ScopeType, ScopeTypeService } from "../scopes/scope-type-service";

@autoinject
export class ReportedServiceDataItemEdit extends ServiceDataItemHandler {

	@bindable serviceStructureId: string;
	@bindable customerId: string;
	@bindable scopeTypeGroup: ScopeTypeGroup;
	@bindable structureName: string;

	@observable availableDataItems: DataItemOption[];
	@observable availableScopeTypes: ScopeType[];

	services: NodeModel[];
	serviceDataItems: IServiceDataItems = {};
	loading = true;

	constructor(
		private service: ServiceStructureService,
		private reportedServiceDataItemService: ReportedServiceDataItemService,
		private customerService: CustomerService,
		private measureService: MeasureService,
		private performanceIndicatorService: PerformanceIndicatorService,
		private scopetypeService: ScopeTypeService,
		container: Container
	) {
		super();

		container.registerInstance(ServiceDataItemHandler, this);
	}

	async scopeTypeGroupChanged(newValue, oldValue): Promise<any> {
		this.loading = true;

		this.services = await this.service.getServiceStructureForCustomer(
			this.serviceStructureId, this.customerId
		);
		const items = await this.reportedServiceDataItemService.getReportedServiceDataItemsForServiceStructure(
			this.scopeTypeGroup.id,
			this.serviceStructureId
		);

		this.serviceDataItems = this.createServiceDataItems(items);

		const allScopeTypes = await this.scopetypeService.getScopeTypes();
		this.availableScopeTypes = filter(
			allScopeTypes,
			st => st.scopeTypeGroupId == this.scopeTypeGroup.id
		).sort(st => st.sortOrder);

		this.loading = false;
	}

	async bind() {
		const availableMeasures = (await this.measureService.getMeasures()).map(
			measure => {
				const item = new DataItemOption(measure);
				item.type = ReportedServiceDataItemType.Measure;
				return item;
			}
		);

		const performanceIndicators = (await this.performanceIndicatorService.getPerformanceIndicators()).map(
			(pi: IPerformanceIndicatorAM) => {
				const item = new DataItemOption(pi);
				item.specifier = pi.code;
				item.type = ReportedServiceDataItemType.PerformanceIndicator;
				return item;
			}
		);

		this.availableDataItems = [].concat(
			availableMeasures,
			performanceIndicators
		);

		this.attachDataItem = this.onAttachMeasure;
		this.setInputTypeState = this.onSetInputTypeState;
		this.detachDataItem = this.onDetachMeasure;
		this.changeDescription = this.onChangeDescription;
		this.changeCustomerComparisonState = this.onChangeCustomerComparisonState;
	}

	attached() {
		this.customerService.selectedCustomerId = this.customerId;
	}

	private resolveSortOrder(
		attachedReportedServiceDataItem: DataItemSelectionResult
	) {
		const items = this.serviceDataItems[
			attachedReportedServiceDataItem.serviceId
		];
		if (items) {
			return max(items.map(i => i.sortOrder)) + 1;
		}
		return 1;
	}

	async onChangeCustomerComparisonState(dataItem: AttachedDataItem): Promise<any> {
		throw new Error('State change not allowed.');
	}

	async onAttachMeasure(
		attachedReportedServiceDataItem: DataItemSelectionResult
	): Promise<any> {
		const scopeTypeGroupId = this.scopeTypeGroup.id;
		const customerId = this.customerService.selectedCustomerId;

		const newReportedServiceDataItem = new AttachedDataItem({
			serviceId: attachedReportedServiceDataItem.serviceId,
			dataItemId: attachedReportedServiceDataItem.dataItemId,
			sortOrder: this.resolveSortOrder(attachedReportedServiceDataItem),
			scopeTypeGroupId: scopeTypeGroupId,
			type: attachedReportedServiceDataItem.type
		});

		await this.reportedServiceDataItemService.addReportedServiceDataItem(
			newReportedServiceDataItem,
			customerId
		);

		const items = await this.reportedServiceDataItemService.getReportedServiceDataItemsForService(
			attachedReportedServiceDataItem.serviceId,
			customerId,
			scopeTypeGroupId
		);

		this.serviceDataItems[
			attachedReportedServiceDataItem.serviceId
		] = items.map(i => new AttachedDataItem(i));
	}

	async onChangeDescription(dataItem: AttachedDataItem): Promise<any> {
		await this.reportedServiceDataItemService.changeDataItemDescription(
			dataItem,
			this.customerId
		);
	}

	async onDetachMeasure(onDetachDataItem: AttachedDataItem) {
		await this.reportedServiceDataItemService.removeReportedServiceDataItem(
			onDetachDataItem
		);
		const updatedItemsList = await this.reportedServiceDataItemService.getReportedServiceDataItemsForService(
			onDetachDataItem.serviceId,
			this.customerId,
			this.scopeTypeGroup.id
		);

		this.serviceDataItems[
			onDetachDataItem.serviceId
		] = updatedItemsList.map(i => new AttachedDataItem(i));
	}

	public onSetInputTypeState(
		dataItem: AttachedDataItem,
		inputType: string,
		isCollected: boolean
	): Promise<any> {

		if (dataItem.isForCustomerComparison && this.reportedServiceDataItemService.isRemovingLastInputType(dataItem, inputType, isCollected)) {
			return Promise.resolve(new Error(ReportedServiceDataItemService.error_CannotRemoveLastInputFromCustomerComparisonMeasure));
		}

		return this.reportedServiceDataItemService.updateReportedServiceDataItemInputType(
			dataItem.serviceId,
			dataItem.id,
			inputType,
			isCollected
		);
	}	

	@computedFrom("structureName", "scopeType")
	get title() {
		return (
			this.structureName +
			(this.scopeTypeGroup != null
				? " / " + this.scopeTypeGroup.name
				: "")
		);
	}
}
